import { useCallback, useEffect, useState } from 'react';
type FocusBlurApproach = 'none' | 'focus' | 'blur' | 'both';
/**
 * Hook for checking the page visibility. Will return true if the page is visible and false if it is not.
 *
 * @param focusBlurApproach
 *
 * Useful if user has a layout that never changes the page visibility (the browser is idly always open)
 * and you want to check if the user is focused on the page or not.
 *
 * ```
 * 'blur' will listen to only the blur event
 * 'focus' will listen only to the focus event
 * 'both' will listen to both "focus" and "blur" events
 * 'none' will not listen to any event, is default
 * ```
 * @returns true if the page is visible and false if it is not.
 */
export const usePageVisibility = (focusBlurApproach: FocusBlurApproach = 'none') => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const handleVisibilityChange = useCallback(() => {
    setIsVisible(!document.hidden);
  }, []);
  useEffect(() => {
    const shouldListenToFocusEvent = focusBlurApproach === 'both' || focusBlurApproach === 'focus';
    const shouldListenToBlurEvent = focusBlurApproach === 'both' || focusBlurApproach === 'blur';
    if (shouldListenToFocusEvent) {
      window.addEventListener('focus', handleVisibilityChange);
    }
    if (shouldListenToBlurEvent) {
      window.addEventListener('blur', handleVisibilityChange);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      window.removeEventListener('blur', handleVisibilityChange);
    };
  }, [handleVisibilityChange, focusBlurApproach]);
  return isVisible;
};
